import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  collection,
  addDoc,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db, auth } from "../utils/firebase";
import "../styles/BookingForm.css";

/* =====================================================
   COLLECTION RESOLVER
   ===================================================== */

const COLLECTION_MAP = {
  catering: "cateringBookings",
  chef: "chefBookings",
  dailyfood: "dailyFoodBookings",
  dailyfoods: "dailyFoodBookings",
  cloudkitchen: "dailyFoodBookings",
};

const resolveCollection = (type = "") => {
  const key = type.toLowerCase().replace(/[^a-z]/g, "");
  return COLLECTION_MAP[key] || "cateringBookings";
};

/* =====================================================
   INITIAL FORM STATE
   ===================================================== */

const getInitialFormData = (menuName = "") => ({
  name: "",
  phone: "",
  date: "",
  time: "",
  guests: "",
  venue: "",
  notes: "",
  menu: menuName,
  total: 0,
});

/* =====================================================
   COMPONENT
   ===================================================== */

const BookingForm = ({
  type = "Catering",
  menuName = "",
  price = 0,
}) => {
  const [formData, setFormData] = useState(
    getInitialFormData(menuName)
  );
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [bookings, setBookings] = useState([]);

  const collectionName = useMemo(
    () => resolveCollection(type),
    [type]
  );

  /* =====================================================
     AUTOFILL USER DATA
     ===================================================== */

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    setFormData((prev) => ({
      ...prev,
      name: user.displayName || "",
    }));
  }, []);

  /* =====================================================
     REALTIME USER BOOKINGS
     ===================================================== */

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const q = query(
      collection(db, collectionName),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setBookings(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }))
        );
      },
      (err) => {
        console.error("Realtime booking fetch failed:", err);
      }
    );

    return () => unsub();
  }, [collectionName]);

  /* =====================================================
     FORM CHANGE HANDLER
     ===================================================== */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = { ...prev };

      if (name === "guests") {
        const count = Number(value) || 0;
        updated.guests = value;
        updated.total = price * count;
      } else {
        updated[name] = value;
      }

      return updated;
    });
  };

  /* =====================================================
     VALIDATION
     ===================================================== */

  const validateForm = useCallback(() => {
    const {
      name,
      phone,
      date,
      time,
      guests,
      venue,
    } = formData;

    if (
      !name ||
      !phone ||
      !date ||
      !time ||
      !guests ||
      !venue
    ) {
      return "⚠️ All required fields must be filled.";
    }

    if (!/^\d{10}$/.test(phone)) {
      return "⚠️ Phone number must be exactly 10 digits.";
    }

    if (Number(guests) <= 0) {
      return "⚠️ Guests must be greater than zero.";
    }

    if (new Date(date) < new Date()) {
      return "⚠️ Event date must be in the future.";
    }

    return "";
  }, [formData]);

  /* =====================================================
     SUBMIT HANDLER (PRODUCTION SAFE)
     ===================================================== */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setError("⚠️ Please log in to submit a booking.");
      return;
    }

    setLoading(true);

    try {
      await addDoc(collection(db, collectionName), {
        name: formData.name,
        phone: formData.phone,
        date: formData.date,
        time: formData.time,
        guests: Number(formData.guests),   // ✅ STORED AS NUMBER
        venue: formData.venue,
        notes: formData.notes,
        menu: formData.menu,
        total: Number(formData.total),
        userId: user.uid,
        userEmail: user.email,              // ✅ SINGLE SOURCE
        bookingType: type,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      setSubmitted(true);
      setFormData(getInitialFormData(menuName));
    } catch (err) {
      console.error("Booking error:", err);
      setError("❌ Failed to place booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     SUCCESS UI
     ===================================================== */

  if (submitted) {
    return (
      <div className="booking-success">
        <h3>✅ Booking Confirmed</h3>
        <p>
          Your <strong>{type}</strong> booking has been
          received. Our team will contact you shortly.
        </p>
        <button
          className="submit-btn"
          onClick={() => setSubmitted(false)}
        >
          Make Another Booking
        </button>
      </div>
    );
  }

  /* =====================================================
     RENDER
     ===================================================== */

  return (
    <div className="booking-container">
      <form
        className="booking-form"
        onSubmit={handleSubmit}
        noValidate
      >
        <h2 className="form-title">Book {type}</h2>

        {error && <div className="error-msg">{error}</div>}
        {loading && <div className="loading-msg">⏳ Submitting…</div>}

        <input name="name" placeholder="Full Name" value={formData.name} onChange={handleChange} />
        <input name="phone" placeholder="Phone" value={formData.phone} onChange={handleChange} />
        <input type="date" name="date" value={formData.date} onChange={handleChange} />
        <input type="time" name="time" value={formData.time} onChange={handleChange} />
        <input type="number" name="guests" placeholder="Guests" value={formData.guests} onChange={handleChange} />
        <input name="venue" placeholder="Venue" value={formData.venue} onChange={handleChange} />
        <textarea name="notes" placeholder="Notes" value={formData.notes} onChange={handleChange} />

        {price > 0 && (
          <input value={`₹${formData.total}`} readOnly />
        )}

        <button className="submit-btn" disabled={loading}>
          {loading ? "Submitting…" : "Submit Booking"}
        </button>
      </form>

      {bookings.length > 0 && (
        <div className="booking-list">
          <h3>Your Recent Bookings</h3>
          <ul>
            {bookings.map((b) => (
              <li key={b.id}>
                {b.date} {b.time} — {b.guests} guests ({b.status})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BookingForm;
